import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {CardGroupModel} from '../../../models/api/cardGroupModel';
import {CardModel} from '../../../models/api/cardModel';
import {removeCardGroupFromCardAsyncAction} from '../../../store/data/cardGroups/cardGroupsActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {AppRoutesPrefixesEnum} from '../../appRoutesPrefixes';
import {TooltipExplicitCondition} from '../../common/tooltips/tooltipExplicitCondition';
import {useContactDetailsContext} from '../contactDetailsContext';
import {
  buildContactDetailsEditCardGroupEvent,
  ContactDetailsCardGroupActionsEnum,
} from '../contactDetailsTrackingHelpers';
import {ContactHeaderPill} from './contactHeaderPill';

/*
 * Constants.
 */

const DropdownContainerWidth = 220;

/*
 * Props.
 */

interface ContactHeaderGroupProps {
  group: CardGroupModel;
  card: CardModel;
}

/*
 * Styles.
 */

const StyledContactHeaderPill = styled(ContactHeaderPill)`
  display: flex;
  flex-flow: row;
  margin-top: 4px;
  margin-right: 4px;
  overflow: hidden;
`;

const StyledNameDiv = styled.div`
  flex: 1;
  ${ellipsis()};
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  overflow: hidden;
`;

/*
 * Component.
 */

export const ContactHeaderGroup: FC<ContactHeaderGroupProps> = (props) => {
  const theme = useUserTheme();
  const {group, card} = props;
  const nameRef = useRef<HTMLDivElement>(null);
  const [isTooltipEnabled, setTooltipEnabled] = useState(false);
  const isPrivateGroup = isTeammateNamespace(group.namespace);
  const dispatch = useAppDispatch();

  const contactDetailsContext = useContactDetailsContext();
  const {onTrackEvent} = contactDetailsContext;

  const onNavigate = useCallback(() => {
    dispatch(navigateToLinkAction(`/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/${group.id}`));
  }, [dispatch, group]);

  const onRemove = useCallback(() => {
    dispatch(removeCardGroupFromCardAsyncAction(group, card.id));
    if (onTrackEvent) {
      onTrackEvent(
        buildContactDetailsEditCardGroupEvent(ContactDetailsCardGroupActionsEnum.DELETE, card, group),
      );
    }
  }, [dispatch, onTrackEvent, group, card]);

  // Check if we should display the groups name in a tooltip if it is ellipsed
  useLayoutEffect(() => {
    if (!nameRef.current) {
      return;
    }
    setTooltipEnabled(nameRef.current.scrollWidth > nameRef.current.offsetWidth);
  }, [setTooltipEnabled]);

  return (
    <StyledPopoverCoordinator
      interactionId={`contact-details-group-${group.id}-pill`}
      isDisabled={contactDetailsContext.isReadOnly}
      renderButton={(isModalOpen: boolean) => (
        <TooltipCoordinator render={() => group.name} placement="top">
          <TooltipExplicitCondition isEnabled={isTooltipEnabled} />
          <StyledContactHeaderPill
            textColor={theme.greys.shade80}
            backgroundColor={isModalOpen ? theme.greys.shade30 : theme.greys.shade20}
            hoverBackgroundColor={theme.greys.shade30}
            isDisabled={contactDetailsContext.isReadOnly}
          >
            <StyledNameDiv ref={nameRef}>{group.name}</StyledNameDiv>
            {isPrivateGroup && <StyledIcon name="lockFilledNewTiny" color={theme.greys.shade70} />}
          </StyledContactHeaderPill>
        </TooltipCoordinator>
      )}
      renderModal={(popoverProps) => (
        <Dropdown {...popoverProps} placement="bottom-start" width={DropdownContainerWidth}>
          <DropdownList>
            <DropdownItem key="navigate-to-group-key" onSelect={() => onNavigate()}>
              <DropdownItemContent>
                <Trans id="contacts-header-group-go-to-group">Go to list</Trans>
              </DropdownItemContent>
            </DropdownItem>
            <DropdownItem
              interactionId="contact-details-group-dropdown-remove-group"
              key="remove-group-key"
              onSelect={() => {
                onRemove();
                popoverProps.onRequestClose();
              }}
            >
              <DropdownItemContent>
                <Trans id="contacts-header-group-remove">Remove</Trans>
              </DropdownItemContent>
            </DropdownItem>
          </DropdownList>
        </Dropdown>
      )}
    />
  );
};
